/**
 * Line Items Table Component
 * Displays invoice line items with TanStack Table
 * AC-2: Line Items Table
 */

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CurrencyFormatter } from '@/components/common/CurrencyFormatter';
import { LineItemDetails } from '@/types';
import { useMemo } from 'react';

interface LineItemsTableProps {
  lineItems: LineItemDetails[];
}

export const LineItemsTable = ({ lineItems }: LineItemsTableProps) => {
  const columns = useMemo<ColumnDef<LineItemDetails>[]>(
    () => [
      {
        accessorKey: 'description',
        header: 'Product Name / Description',
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-gray-900">{row.original.description}</p>
            {row.original.description && (
              <p className="text-xs text-gray-500 mt-1">Product Code: {row.original.description.substring(0, 20)}</p>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'batchCode',
        header: 'Batch Code',
        cell: ({ row }) => (
          <div>
            {row.original.batchCode ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {row.original.batchCode}
              </span>
            ) : (
              <span className="text-xs text-gray-400">-</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'quantity',
        header: () => <div className="text-right">Quantity</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <div className="font-medium">
              {row.original.quantity.toLocaleString('en-IN')}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {row.original.unit || 'Units'}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'unitPrice',
        header: () => <div className="text-right">Unit Price</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <CurrencyFormatter amount={row.original.unitPrice} />
          </div>
        ),
      },
      {
        accessorKey: 'discount',
        header: () => <div className="text-right">Discount</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <CurrencyFormatter amount={row.original.discount} />
            {row.original.discountPercentage > 0 && (
              <span className="text-xs text-muted-foreground ml-1">
                ({row.original.discountPercentage}%)
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'taxAmount',
        header: () => <div className="text-right">Tax</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <CurrencyFormatter amount={row.original.taxAmount} />
            {row.original.taxRate > 0 && (
              <span className="text-xs text-muted-foreground ml-1">
                ({row.original.taxRate}%)
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'lineTotal',
        header: () => <div className="text-right">Total</div>,
        cell: ({ row }) => (
          <div className="text-right font-medium">
            <CurrencyFormatter amount={row.original.lineTotal} />
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: lineItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Line Items</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No line items found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
